import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Building2, Mail, Phone, MapPin, Search, Filter, 
  MoreVertical, CheckCircle, XCircle, Clock, Users,
  FileText, Upload, Download, Trash2, Edit, X, CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokersPage = () => {
  const [brokers, setBrokers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBroker, setEditingBroker] = useState(null);
  const [selectedBrokers, setSelectedBrokers] = useState([]);
  
  // Advanced Search
  const [citySearch, setCitySearch] = useState('');
  const [zipSearch, setZipSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [states, setStates] = useState([]);
  const [totalBrokers, setTotalBrokers] = useState(0);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [brokersPerPage] = useState(100);
  
  // Bulk Email Modal
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    mc_number: '',
    dot_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });

  const fetchBrokers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (stateFilter) params.append('state', stateFilter);
      if (citySearch) params.append('city', citySearch);
      if (zipSearch) params.append('zip_code', zipSearch);
      params.append('limit', brokersPerPage.toString());
      params.append('page', currentPage.toString());
      
      const [brokersRes, countRes] = await Promise.all([
        axios.get(`${API}/brokers?${params.toString()}`),
        axios.get(`${API}/brokers/count?${params.toString()}`)
      ]);
      
      setBrokers(brokersRes.data);
      setTotalBrokers(countRes.data.total || brokersRes.data.length);
    } catch (error) {
      console.error('Error fetching brokers:', error);
      toast.error('Failed to fetch brokers');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, stateFilter, citySearch, zipSearch, currentPage, brokersPerPage]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/brokers/states`);
      // API returns {states: [...], total_states: N}
      setStates(response.data.states || response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/brokers/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching broker stats:', error);
    }
  };

  useEffect(() => {
    fetchBrokers();
    fetchStats();
    fetchStates();
  }, [fetchBrokers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBroker) {
        await axios.put(`${API}/brokers/${editingBroker.id}`, formData);
        toast.success('Broker updated successfully');
      } else {
        await axios.post(`${API}/brokers`, formData);
        toast.success('Broker created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchBrokers();
      fetchStats();
    } catch (error) {
      console.error('Error saving broker:', error);
      toast.error(error.response?.data?.detail || 'Failed to save broker');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      mc_number: '',
      dot_number: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      notes: '',
    });
    setEditingBroker(null);
  };

  const handleEdit = (broker) => {
    setEditingBroker(broker);
    setFormData({
      company_name: broker.company_name || '',
      contact_name: broker.contact_name || '',
      email: broker.email || '',
      phone: broker.phone || '',
      mc_number: broker.mc_number || '',
      dot_number: broker.dot_number || '',
      address: broker.address || '',
      city: broker.city || '',
      state: broker.state || '',
      zip_code: broker.zip_code || '',
      notes: broker.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (brokerId) => {
    if (!window.confirm('Are you sure you want to delete this broker?')) return;
    try {
      await axios.delete(`${API}/brokers/${brokerId}`);
      toast.success('Broker deleted successfully');
      fetchBrokers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete broker');
    }
  };

  const handleStatusUpdate = async (brokerId, status) => {
    try {
      await axios.put(`${API}/brokers/${brokerId}/status?status=${status}`);
      toast.success('Status updated');
      fetchBrokers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedBrokers.length === 0) {
      toast.error('Select brokers first');
      return;
    }
    try {
      await axios.post(`${API}/brokers/bulk-status?status=${status}`, selectedBrokers);
      toast.success(`Updated ${selectedBrokers.length} brokers`);
      setSelectedBrokers([]);
      fetchBrokers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update brokers');
    }
  };

  const toggleSelectBroker = (brokerId) => {
    setSelectedBrokers(prev => 
      prev.includes(brokerId) 
        ? prev.filter(id => id !== brokerId)
        : [...prev, brokerId]
    );
  };

  const selectAllBrokers = () => {
    if (selectedBrokers.length === brokers.length) {
      setSelectedBrokers([]);
    } else {
      setSelectedBrokers(brokers.map(b => b.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/15 text-blue-500',
      contacted: 'bg-amber-500/15 text-amber-500',
      active: 'bg-emerald-500/15 text-emerald-500',
      inactive: 'bg-slate-500/15 text-slate-400',
    };
    return styles[status] || styles.new;
  };

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card className="glass-card" data-testid={`broker-stat-${title.toLowerCase()}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-black font-heading text-foreground mt-1">{value}</p>
          </div>
          <div className={`p-3 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="brokers-page-container">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Broker CRM</h1>
            <p className="text-muted-foreground mt-2">Manage broker relationships and outreach</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="glow-button" data-testid="add-broker-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Broker
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass-card">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading">
                  {editingBroker ? 'Edit Broker' : 'Add New Broker'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="broker-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      required
                      data-testid="input-company-name"
                    />
                  </div>
                  <div>
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      data-testid="input-contact-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>MC Number</Label>
                    <Input
                      value={formData.mc_number}
                      onChange={(e) => setFormData({...formData, mc_number: e.target.value})}
                      placeholder="MC-123456"
                      data-testid="input-mc-number"
                    />
                  </div>
                  <div>
                    <Label>DOT Number</Label>
                    <Input
                      value={formData.dot_number}
                      onChange={(e) => setFormData({...formData, dot_number: e.target.value})}
                      placeholder="DOT-123456"
                      data-testid="input-dot-number"
                    />
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    data-testid="input-address"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      maxLength={2}
                      placeholder="CA"
                      data-testid="input-state"
                    />
                  </div>
                  <div>
                    <Label>ZIP Code</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      data-testid="input-zip"
                    />
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional notes..."
                    data-testid="input-notes"
                  />
                </div>

                <Button type="submit" className="w-full glow-button" data-testid="submit-broker-button">
                  {editingBroker ? 'Update Broker' : 'Add Broker'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard icon={Building2} title="Total" value={stats.total} color="#3B82F6" />
            <StatCard icon={Clock} title="New" value={stats.new} color="#3B82F6" />
            <StatCard icon={Mail} title="Contacted" value={stats.contacted} color="#F59E0B" />
            <StatCard icon={CheckCircle} title="Active" value={stats.active} color="#10B981" />
            <StatCard icon={XCircle} title="Inactive" value={stats.inactive} color="#64748B" />
          </div>
        )}

        {/* Advanced Filters & Actions */}
        <Card className="glass-card">
          <CardContent className="p-4 space-y-4">
            {/* Search Row */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by company, email, contact, phone, or MC#..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="broker-search-input"
                />
              </div>
              <Button onClick={fetchBrokers} variant="outline" data-testid="refresh-brokers-button">
                Refresh
              </Button>
            </div>
            
            {/* Advanced Filters Row */}
            <div className="flex flex-wrap gap-3 items-center p-3 bg-slate-800/50 rounded-lg">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="City..."
                value={citySearch}
                onChange={(e) => { setCitySearch(e.target.value); setCurrentPage(1); }}
                className="w-32"
                data-testid="broker-city-filter"
              />
              <Input
                placeholder="ZIP..."
                value={zipSearch}
                onChange={(e) => { setZipSearch(e.target.value); setCurrentPage(1); }}
                className="w-24"
                data-testid="broker-zip-filter"
              />
              <select
                value={stateFilter}
                onChange={(e) => { setStateFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                data-testid="broker-state-filter"
              >
                <option value="">All States</option>
                {states.map(s => (
                  <option key={s.state || s} value={s.state || s}>
                    {s.state || s} {s.count ? `(${s.count.toLocaleString()})` : ''}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                data-testid="broker-status-filter"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {(citySearch || zipSearch || stateFilter || statusFilter) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setCitySearch('');
                    setZipSearch('');
                    setStateFilter('');
                    setStatusFilter('');
                    setCurrentPage(1);
                  }}
                >
                  <X className="w-4 h-4 mr-1" /> Clear Filters
                </Button>
              )}
            </div>
            
            {/* Pagination Info */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing {brokers.length} of {totalBrokers.toLocaleString()} brokers
                {(citySearch || zipSearch || stateFilter) && ' (filtered)'}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </Button>
                <span className="px-3">Page {currentPage}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={brokers.length < brokersPerPage}
                >
                  Next →
                </Button>
              </div>
            </div>

            {selectedBrokers.length > 0 && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="text-sm text-foreground font-bold">{selectedBrokers.length} selected</span>
                <div className="flex gap-2 ml-auto">
                  <Button 
                    size="sm" 
                    onClick={() => setBulkEmailOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="bulk-email-brokers"
                  >
                    <Mail className="w-3 h-3 mr-1" />
                    Email Selected
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleBulkStatusUpdate('contacted')}
                    data-testid="bulk-mark-contacted"
                  >
                    Mark Contacted
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleBulkStatusUpdate('active')}
                    data-testid="bulk-mark-active"
                  >
                    Mark Active
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setSelectedBrokers([])}
                    data-testid="clear-selection"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Broker List */}
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Brokers ({brokers.length})
              </CardTitle>
              {brokers.length > 0 && (
                <Button variant="ghost" size="sm" onClick={selectAllBrokers} data-testid="select-all-brokers">
                  {selectedBrokers.length === brokers.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading brokers...</p>
                </div>
              </div>
            ) : brokers.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-heading text-foreground mb-2">No Brokers Yet</h3>
                <p className="text-sm text-muted-foreground mb-6">Add your first broker to get started with outreach</p>
                <Button onClick={() => setDialogOpen(true)} className="glow-button" data-testid="add-first-broker">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Broker
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {brokers.map((broker) => (
                  <div
                    key={broker.id}
                    className={`flex items-center gap-4 p-4 rounded-lg bg-background/50 hover:bg-accent/50 transition-colors ${
                      selectedBrokers.includes(broker.id) ? 'ring-2 ring-primary bg-primary/5' : ''
                    }`}
                    data-testid={`broker-row-${broker.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedBrokers.includes(broker.id)}
                      onChange={() => toggleSelectBroker(broker.id)}
                      className="w-4 h-4 rounded border-border"
                      data-testid={`broker-checkbox-${broker.id}`}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground truncate">{broker.company_name}</p>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${getStatusBadge(broker.status)}`}>
                          {broker.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {broker.contact_name && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {broker.contact_name}
                          </span>
                        )}
                        {(broker.city || broker.state) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {broker.city}{broker.city && broker.state ? ', ' : ''}{broker.state}
                          </span>
                        )}
                        {broker.mc_number && (
                          <span className="font-mono">MC: {broker.mc_number}</span>
                        )}
                      </div>
                    </div>

                    {/* Contact Buttons */}
                    <div className="flex items-center gap-1">
                      {broker.phone && (
                        <>
                          <a 
                            href={`openphone://dial?number=${encodeURIComponent(broker.phone)}&action=call`}
                            className="px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-white text-xs font-bold"
                            title="Call via Quo"
                          >
                            📱
                          </a>
                          <a 
                            href={`tel:${broker.phone}`}
                            className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-xs font-bold"
                            title={`Call ${broker.phone}`}
                          >
                            <Phone className="w-3 h-3" />
                            {broker.phone}
                          </a>
                        </>
                      )}
                      <a 
                        href={`mailto:${broker.email}?subject=Partnership Opportunity - Tempus Dispatch`}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-xs font-bold"
                        title={`Email ${broker.email}`}
                      >
                        <Mail className="w-3 h-3" />
                      </a>
                    </div>

                    <div className="flex items-center gap-1 border-l border-border pl-2">
                      {broker.status === 'new' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusUpdate(broker.id, 'contacted')}
                          data-testid={`mark-contacted-${broker.id}`}
                          className="text-xs"
                        >
                          Mark Contacted
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(broker)}
                        data-testid={`edit-broker-${broker.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(broker.id)}
                        data-testid={`delete-broker-${broker.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BrokersPage;
